// Parts coming in from a vendor invoice: photo → AI reads it (or type it) → check every line → Confirm.
// Parts go into stock (new ones created), one vendor bill is made: Paid now (most of the time) or Unpaid (pay later).
import { useRef, useState } from 'react';
import { api, useApi, dollars, toCents, centsInput, uploadPhoto, pageTiles, today, mayI, payMethods, type Row } from '../lib';
import { isSheet, readSheet, sheetText } from '../sheet';
import { Button, Modal, Icon, Badge, useToast, Table, confirmAction, VendorPick } from '../ui';

type Line = { sku: string; description: string; qty: number | string; unit_cost: string; price: string; part_id: string | null; old_cost: number | null; part_name: string; price_changed?: boolean };


export function ScanInvoiceModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [step, setStep] = useState<'pick' | 'review'>('pick');
  const [busy, setBusy] = useState(false);
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [h, setH] = useState({ vendor: '', invoice_no: '', date: today(), freight: '', tax: '', total: 0 });
  const [lines, setLines] = useState<Line[]>([]);
  const [paid, setPaid] = useState(true);
  const [method, setMethod] = useState('Cash');
  const [due, setDue] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const galRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const vendors = useApi<Row[]>('/api/vendors');
  const parts = useApi<Row[]>('/api/parts');

  async function read(list: File[]) {
    if (!list.length) return;
    setBusy(true);
    try {
      let r: Row;
      const sheet = list.find(isSheet);
      if (sheet) {
        // Excel / CSV invoice: the rows go to the AI as text — no photo.
        const sh = await readSheet(sheet);
        if (!sh.rows.length) throw new Error('The spreadsheet is empty');
        setPhotoId(null);
        r = await api<Row>('/api/scan/vendor-invoice', { body: { text: sheetText(sh) } });
      } else {
        const [first, ...more] = list.slice(0, 8);
        const { id } = await uploadPhoto(first, { kind: 'receipt', purpose: 'expense' }, 2400);
        setPhotoId(id);
        // One photo of a full page: also send it cut in two (bigger print). Several photos: page 1, page 2 …
        const tiles = more.length ? [] : await Promise.all((await pageTiles(first)).map(async t => (await uploadPhoto(t, { kind: 'receipt', purpose: 'expense' }, 2400)).id));
        const pages = await Promise.all(more.map(async f => (await uploadPhoto(f, { kind: 'receipt', purpose: 'expense' }, 2400)).id));
        r = await api<Row>('/api/scan/vendor-invoice', { body: { photo_id: id, ...(tiles.length ? { tiles } : {}), ...(pages.length ? { pages } : {}) } });
      }
      setH({ vendor: r.vendor, invoice_no: r.invoice_no, date: r.date, freight: r.freight ? centsInput(r.freight) : '', tax: r.tax ? centsInput(r.tax) : '', total: r.total });
      setLines((r.lines as Row[]).map(l => ({ sku: l.sku, description: l.description, qty: l.qty, unit_cost: centsInput(l.unit_cost), price: l.price ? centsInput(l.price) : '', part_id: l.part_id, old_cost: l.old_cost, part_name: l.part_name, price_changed: l.price_changed })));
      setStep('review');
      if (!(r.lines as Row[]).length) toast('No part lines found — add them by hand', 'err');
    } catch (e) {
      toast((e as Error).message + ' — you can type the invoice instead.', 'err');
    } finally { setBusy(false); }
  }
  const set = (i: number, k: keyof Line, val: string) => setLines(x => x.map((l, j) => {
    if (j !== i) return l;
    const n = { ...l, [k]: val } as Line;
    if (k === 'sku') { // typed a part # → match the catalog
      const p = (parts.data || []).find(p => String(p.sku).toLowerCase() === val.trim().toLowerCase());
      n.part_id = p?.id || null; n.part_name = p?.name || ''; n.old_cost = p ? p.cost : null;
      if (p && !n.description) n.description = p.name;
      if (p && !n.price) n.price = centsInput(p.price);
    }
    return n;
  }));
  const sub = lines.reduce((a, l) => a + (Number(l.qty) || 0) * toCents(l.unit_cost), 0);
  const total = sub + toCents(h.freight) + toCents(h.tax);

  return (
    <Modal title={step === 'pick' ? 'Parts in — vendor invoice' : `Check the invoice${h.vendor ? ' — ' + h.vendor : ''}`} onClose={onClose} wide>
      {step === 'pick' ? (
        <div className="modal-pad">
          <p>Take a photo of the vendor's invoice (or pick the PDF / Excel they emailed). Photo tips: flat paper, good light, the whole page in the picture, no shadow — a PDF or Excel from the vendor's website is the most exact. AI reads the part numbers, quantities and prices — you check them before anything is saved.</p>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={e => { void read(Array.from(e.target.files || [])); e.target.value = ''; }} />
          <input ref={galRef} type="file" multiple accept="image/*,application/pdf,.xlsx,.xlsm,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={e => { void read(Array.from(e.target.files || [])); e.target.value = ''; }} />
          <div className="scan-choices">
            <button className="scan-choice" disabled={busy} onClick={() => fileRef.current?.click()}><Icon name="camera" size={26} /><b>{busy ? 'Reading the invoice…' : 'Take a photo of the invoice'}</b><span>AI fills in every line</span></button>
            <button className="scan-choice" disabled={busy} onClick={() => galRef.current?.click()}><Icon name="image" size={26} /><b>PDF, Excel or saved photo</b><span>From email, files or the gallery — several photos = several pages</span></button>
            <button className="scan-choice" disabled={busy} onClick={() => { setLines([{ sku: '', description: '', qty: 1, unit_cost: '', price: '', part_id: null, old_cost: null, part_name: '' }]); setStep('review'); }}><Icon name="edit" size={26} /><b>Type it in</b><span>No photo</span></button>
          </div>
        </div>
      ) : (
        <div className="modal-pad scan-review">
          <div className="scan-head">
            <label className="field"><span>Vendor *</span><VendorPick value={h.vendor} onChange={x => setH({ ...h, vendor: x })} /></label>
            <label className="field"><span>Invoice #</span><input value={h.invoice_no} onChange={e => setH({ ...h, invoice_no: e.target.value })} /></label>
            <label className="field"><span>Date</span><input type="date" value={h.date} onChange={e => setH({ ...h, date: e.target.value })} /></label>
          </div>
          <table className="scan-lines"><thead><tr><th>Part #</th><th>Description</th><th>Qty</th><th>Cost each</th><th>Sell price</th><th /></tr></thead><tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td><input value={l.sku} onChange={e => set(i, 'sku', e.target.value)} placeholder="Part #" />
                  {l.part_id ? <small className="good-text">✓ in stock{l.part_name && l.part_name !== l.description ? ` as "${l.part_name}"` : ''}</small> : l.sku ? <small className="warn-text">+ new part</small> : null}</td>
                <td><input value={l.description} onChange={e => set(i, 'description', e.target.value)} placeholder="Description" /></td>
                <td><input className="num" value={l.qty} onChange={e => set(i, 'qty', e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric" /></td>
                <td><input className="num" value={l.unit_cost} onChange={e => set(i, 'unit_cost', e.target.value)} inputMode="decimal" placeholder="0.00" />
                  {l.old_cost !== null && l.old_cost > 0 && toCents(l.unit_cost) !== l.old_cost && <small className="danger">⚠ was {dollars(l.old_cost)}</small>}</td>
                <td><input className="num" value={l.price} onChange={e => set(i, 'price', e.target.value)} inputMode="decimal" placeholder="auto" /></td>
                <td><button className="icon-btn" title="Remove line" onClick={() => setLines(x => x.filter((_, j) => j !== i))}><Icon name="trash" size={15} /></button></td>
              </tr>))}
          </tbody></table>
          <Button icon="plus" onClick={() => setLines(x => [...x, { sku: '', description: '', qty: 1, unit_cost: '', price: '', part_id: null, old_cost: null, part_name: '' }])}>Add line</Button>
          <div className="scan-totals">
            <label>Freight <input className="num" value={h.freight} onChange={e => setH({ ...h, freight: e.target.value })} inputMode="decimal" placeholder="0.00" /></label>
            <label>Tax <input className="num" value={h.tax} onChange={e => setH({ ...h, tax: e.target.value })} inputMode="decimal" placeholder="0.00" /></label>
            <b>Total {dollars(total)}</b>{h.total > 0 && Math.abs(h.total - total) > 1 && <small className="danger"> · invoice says {dollars(h.total)} — check the lines</small>}
          </div>
          <div className="paid-pick">
            <button className={paid ? 'on' : ''} onClick={() => setPaid(true)}><b>✓ Paid now</b><span>Cash / card at pickup</span></button>
            <button className={!paid ? 'on unpaid' : ''} onClick={() => setPaid(false)}><b>Unpaid — pay later</b><span>On account, or price to check</span></button>
          </div>
          {paid ? <label className="field"><span>Paid with</span><select value={method} onChange={e => setMethod(e.target.value)}>{payMethods().map(m => <option key={m}>{m}</option>)}</select></label>
            : <label className="field"><span>Pay by (optional)</span><input type="date" value={due} onChange={e => setDue(e.target.value)} /></label>}
          <div className="form-foot"><span className="spacer" /><Button onClick={onClose}>Cancel</Button>
            <Button kind="primary" busy={busy} onClick={async () => {
              if (!h.vendor.trim()) { toast('Which vendor?', 'err'); return; }
              const good = lines.filter(l => (l.sku || l.description) && Number(l.qty) > 0);
              if (!good.length) { toast('Add at least one part', 'err'); return; }
              if (good.some(l => !toCents(l.unit_cost)) && !confirmAction('Some lines have no cost. Save anyway?')) return;
              setBusy(true);
              try {
                const r = await api<Row>('/api/receive-invoice', { body: { vendor: h.vendor, invoice_no: h.invoice_no, date: h.date, freight: toCents(h.freight), tax: toCents(h.tax), paid, method, due_date: paid ? undefined : due || undefined, photo_id: photoId,
                  lines: good.map(l => ({ part_id: l.part_id, sku: l.sku.trim(), description: l.description.trim(), qty: Number(l.qty), unit_cost: toCents(l.unit_cost), price: l.price ? toCents(l.price) : undefined })) } });
                toast(`${good.length} lines in stock${r.created ? `, ${r.created} new parts` : ''} — ${paid ? 'paid' : 'unpaid, shows under Vendors'}`); onDone();
              } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); }
            }}>Confirm — put in stock</Button></div>
        </div>
      )}
    </Modal>
  );
}

/** Inventory → Vendors: who you still owe, and paying it off (one bill or several at once). */
export function UnpaidVendors() {
  const { data, reload } = useApi<Row[]>('/api/unpaid-vendors');
  const [open, setOpen] = useState<string | null>(null);
  if (!data || !data.length) return null;
  return (
    <div className="unpaid-box">
      <b>Unpaid parts</b><span className="muted"> — tap a vendor to see the invoices and mark them paid</span>
      <div className="unpaid-list">{data.map(v => (
        <button key={v.supplier} className="unpaid-v" onClick={() => setOpen(v.supplier)}>
          <b>{v.supplier}</b><span>{dollars(v.owed)}</span><small className={v.overdue > 0 ? 'danger' : 'muted'}>{v.bills} invoice{v.bills > 1 ? 's' : ''}{v.overdue > 0 ? ` · ${dollars(v.overdue)} overdue` : ''}</small>
        </button>))}</div>
      {open && <VendorUnpaidModal supplier={open} onClose={() => { setOpen(null); reload(); }} />}
    </div>
  );
}

function VendorUnpaidModal({ supplier, onClose }: { supplier: string; onClose: () => void }) {
  const { data, reload } = useApi<Row>('/api/vendor-bills?status=open');
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState('Check');
  const [ref, setRef] = useState('');
  const [amt, setAmt] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const canPay = mayI('pay_vendors');
  const bills = ((data?.bills || []) as Row[]).filter(b => b.supplier === supplier && b.balance > 0);
  const chosen = bills.filter(b => pick.has(b.id));
  const pay = async (list: Row[]) => {
    setBusy(true);
    try {
      const part = list.length === 1 && amt ? Math.min(toCents(amt), list[0].balance) : 0;
      for (const b of list) await api(`/api/vendor-bills/${b.id}/payments`, { body: { amount: part || b.balance, paid_on: today(), method, reference: ref } });
      toast(`Paid ${dollars(part || list.reduce((a, b) => a + b.balance, 0))} to ${supplier}`); setPick(new Set()); setAmt(''); reload();
      if (part) return;
      if (list.length === bills.length) onClose();
    } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Unpaid — ${supplier}`} onClose={onClose} wide>
      <div className="modal-pad">
        {!data ? null : !bills.length ? <p>Nothing owed to {supplier}.</p> : <>
          <Table heads={['', 'Invoice #', 'Received', 'Pay by', 'Total', 'Still owed', '']} rows={bills.map(b => [
            canPay ? <input type="checkbox" checked={pick.has(b.id)} onChange={e => setPick(s => { const n = new Set(s); if (e.target.checked) n.add(b.id); else n.delete(b.id); return n; })} aria-label="Pick" /> : '',
            b.reference || '—', b.bill_date, <span className={b.overdue ? 'danger' : ''}>{b.due_date}</span>, dollars(b.amount), <b className="warn-text">{dollars(b.balance)}</b>,
            b.overdue ? <Badge tone="red">Overdue</Badge> : <Badge tone="amber">Open</Badge>])} />
          {canPay ? <div className="pay-bar">
            <label className="field"><span>Paid with</span><select value={method} onChange={e => setMethod(e.target.value)}>{payMethods().map(m => <option key={m}>{m}</option>)}</select></label>
            <label className="field"><span>Check # / reference</span><input value={ref} onChange={e => setRef(e.target.value)} /></label>
            {chosen.length === 1 && <label className="field"><span>Amount (part payment)</span><input value={amt} onChange={e => setAmt(e.target.value)} inputMode="decimal" placeholder={centsInput(chosen[0].balance)} /></label>}
            <Button kind="primary" busy={busy} disabled={!chosen.length} onClick={() => void pay(chosen)}>Mark {chosen.length || ''} paid {chosen.length ? `(${dollars(chosen.reduce((a, b) => a + b.balance, 0))})` : ''}</Button>
            <Button busy={busy} onClick={() => void pay(bills)}>Pay all {dollars(bills.reduce((a, b) => a + b.balance, 0))}</Button>
          </div> : <p className="muted">Ask the owner to mark these paid.</p>}
          <p className="form-note">To pay only part of one invoice, tick it and type the amount.</p>
        </>}
      </div>
    </Modal>
  );
}
